import { Can } from "@casl/react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useConfigurations } from "@via/api/configuration";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import { Spinner } from "@via/components/loading/loading";
import SelectComponent from "@via/components/select/select";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAbility } from "@via/context/ability-context";
import { useAuth } from "@via/hooks/useAuth";
import {
  useUpdateWorkbench,
  useWorkbenchProxyTemplateList,
} from "@via/pages/workbenchs/workbenchs.api";
import { requiredValidation } from "@via/utils/form-validators";
import {
  GetWorkbenchApplicationResponseBody,
  Permissions,
  UpdateWorkbenchApplicationRequestBody,
} from "common";
import { ChangeEvent, ReactNode, useEffect } from "react";
import { useForm } from "react-hook-form";

interface EditWorkbenchFormProps {
  setOpen: (open: boolean) => void;
  workbench?: GetWorkbenchApplicationResponseBody;
}

const EditWorkbenchForm: React.FC<EditWorkbenchFormProps> = ({
  setOpen,
  workbench,
}) => {
  const { user } = useAuth();

  const { data: configurations } = useConfigurations();
  const {
    mutate: updateWorkbench,
    isPending: isUpdateWorkbenchLoading,
    isSuccess: isUpdateWorkbenchSuccess,
  } = useUpdateWorkbench();

  const {
    data: workbenchProxyTemplateList,
    isLoading: isWorkbenchProxyTemplateListLoading,
  } = useWorkbenchProxyTemplateList();

  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: user?.username === workbench?.owner?.username,
    },
    {
      action: "Edit",
      subject: "Input",
      condition:
        user?.username === workbench?.owner?.username &&
        configurations?.ENABLE_SHARE_WITH_EVERYONE_APP,
    },
  ]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<UpdateWorkbenchApplicationRequestBody & { id: number }>({
    mode: "onChange",
    defaultValues: {
      id: workbench?.id,
      name: workbench?.name,
      description: workbench?.description,
      image: workbench?.image,
      port: workbench?.port || undefined,
      env: workbench?.env,
      command: workbench?.command,
      templateId: workbench?.template?.id,
      groupId: workbench?.groupId,
      perms: workbench?.perms,
    },
  });

  const permission = watch("perms");

  useEffect(() => {
    if (permission?.toString() !== Permissions.GroupShared.toString()) {
      setValue("groupId", undefined); // Reset groupId when permission changes
    } else {
      setValue("groupId", workbench?.groupId);
    }
  }, [setValue, permission]);

  useEffect(() => {
    if (isUpdateWorkbenchSuccess) {
      setOpen(false);
    }
  }, [isUpdateWorkbenchSuccess, setOpen]);

  const handlePermissionChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const permission = e.target.value as unknown as Permissions;
    setValue("perms", permission);
  };

  const handleUpdateWorkbench = (
    payload: UpdateWorkbenchApplicationRequestBody & { id: number }
  ): void => {
    const { id, ...rest } = payload;
    console.log({ id, payload: rest });
    updateWorkbench({ id, payload: rest });
  };

  const onSubmit = handleSubmit(handleUpdateWorkbench);

  // Function to render group selection based on permission
  const renderGroupSelection = (): ReactNode => {
    return permission?.toString() === Permissions.GroupShared.toString() ? (
      <UserGroupSelectComponent
        control={control}
        name="groupId"
        {...(workbench?.groupId
          ? {
              missingId: [workbench.groupId],
            }
          : [])}
        rules={requiredValidation("Group")}
        menuPlacement="top"
        errors={errors}
      />
    ) : null;
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Edit Workbench
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => setOpen(false)}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Fill in the details to edit the workbench.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Workbench Name"
                placeholder="Name your Workbench"
                validations={requiredValidation("Workbench Name")}
              />
              <FormTextAreaComponent
                control={control}
                name="description"
                label="Description"
                placeholder="Enter a description for your workbench"
              />
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Workbench Proxy Template
                </label>
                <SelectComponent
                  control={control}
                  name="templateId"
                  rules={requiredValidation("Workbench Proxy Template")}
                  options={
                    workbenchProxyTemplateList?.map(template => {
                      return {
                        label: template.title,
                        value: template.id,
                      };
                    }) ?? []
                  }
                  isLoading={isWorkbenchProxyTemplateListLoading}
                  placeholder="Select Workbench Proxy Template"
                />
                {errors?.templateId && (
                  <span className="text-xs text-rose-500">
                    {errors.templateId.message}
                  </span>
                )}
              </div>
              <FormInputComponent
                control={control}
                name="image"
                label="Container Image"
                placeholder="Specify the image location"
                validations={requiredValidation("Container Image")}
              />
              <FormInputComponent
                control={control}
                name="port"
                label="Container Port"
                placeholder="Enter the container port"
              />
              <FormTextAreaComponent
                control={control}
                name="command"
                label="Container Command"
                placeholder="Enter the container command"
              />
              <FormTextAreaComponent
                control={control}
                name="env"
                label="Container Environment"
                placeholder="Enter the container environment"
              />
              <fieldset>
                <legend className="text-sm font-medium leading-6 text-gray-900">
                  Permission
                </legend>
                <div className="mt-2 space-y-4">
                  <div className="relative flex items-start">
                    <div className="absolute flex h-6 items-center">
                      <input
                        {...register("perms")}
                        id="permission-public"
                        onChange={handlePermissionChange}
                        value={Permissions.Public.toString()}
                        aria-describedby="permission-public-description"
                        type="radio"
                        disabled={!ability.can("Edit", "Input")}
                        checked={
                          permission?.toString() ===
                          Permissions.Public.toString()
                        }
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div
                      className={`pl-7 text-sm leading-6 ${!ability.can("Edit", "Input") ? "opacity-50" : ""}`}>
                      <label
                        htmlFor="permission-public"
                        className="font-medium text-gray-900">
                        Public access
                      </label>
                      <p
                        id="permission-public-description"
                        className="text-gray-500">
                        Everyone with the link will see this workbench.
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register("perms")}
                          id="permission-group"
                          onChange={handlePermissionChange}
                          value={Permissions.GroupShared.toString()}
                          aria-describedby="permission-group-description"
                          type="radio"
                          checked={
                            permission?.toString() ===
                            Permissions.GroupShared.toString()
                          }
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-group"
                          className="font-medium text-gray-900">
                          Private to group members
                        </label>
                        <p
                          id="permission-group-description"
                          className="text-gray-500">
                          Only members of this group would be able to access.
                        </p>
                      </div>
                    </div>
                  </div>
                  {renderGroupSelection()}
                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register("perms")}
                          id="permission-private"
                          onChange={handlePermissionChange}
                          value={Permissions.UserOwned.toString()}
                          aria-describedby="permission-private-description"
                          type="radio"
                          checked={
                            permission?.toString() ===
                            Permissions.UserOwned.toString()
                          }
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-private"
                          className="font-medium text-gray-900">
                          Private to you
                        </label>
                        <p
                          id="permission-private-description"
                          className="text-gray-500">
                          You are the only one able to access this workbench.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-workbench-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setOpen(false)}>
          Cancel
        </button>
        <Can I="View" a="Button" ability={ability}>
          <button
            id="edit-workbench-save"
            type="submit"
            disabled={isUpdateWorkbenchLoading}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <Spinner loading={isUpdateWorkbenchLoading}>Save</Spinner>
          </button>
        </Can>
      </div>
    </form>
  );
};

export default EditWorkbenchForm;
